import React, { useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { StreakCelebrationProps } from '../types';
import StreakErrorBoundary from './StreakErrorBoundary';

const StreakCelebrationModal: React.FC<StreakCelebrationProps> = ({
  isVisible,
  streakEvent,
  onDismiss
}) => {
  const onDismissRef = useRef(onDismiss);

  useEffect(() => {
    onDismissRef.current = onDismiss;
  });

  const handleDismiss = useCallback(() => {
    onDismissRef.current();
  }, []);

  // Auto-dismiss after 4.5 seconds
  useEffect(() => {
    if (!isVisible) return;

    const timer = setTimeout(handleDismiss, 4500);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleDismiss();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisible, handleDismiss]);

  const getDaysLabel = (count: number) => {
    if (count === 1) return 'den';
    if (count >= 2 && count <= 4) return 'dny';
    return 'dní';
  };

  const count = streakEvent.newStreak;
  const label = getDaysLabel(count);

  return (
    <StreakErrorBoundary fallback={null}>
      <AnimatePresence>
        {isVisible && (
          <motion.div
            key="streak-banner"
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            onClick={handleDismiss}
            className="fixed top-4 inset-x-0 z-[9999] flex justify-center pointer-events-none"
          >
            <div className="flex items-center gap-3 md:gap-4 bg-card border border-border rounded-2xl shadow-lg px-4 md:px-6 py-2.5 md:py-3 pointer-events-auto cursor-pointer">
              {/* Lottie animation */}
              <div className="w-12 h-12 md:w-16 md:h-16 flex-shrink-0">
                <DotLottieReact
                  src="/materials/12152388.lottie"
                  loop
                  autoplay
                  speed={0.8}
                  className="w-full h-full"
                />
              </div>

              {/* Streak text */}
              <span className="text-sm md:text-base font-semibold text-foreground whitespace-nowrap">
                {count} {label} v řadě!
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </StreakErrorBoundary>
  );
};

export default StreakCelebrationModal;